import { useState } from 'react'
import { getCreatorAIInsights } from '../lib/supabase'

export default function CreatorAIInsights({ creatorName, tiktokData, instaData, isMobile }) {
  const [loading, setLoading] = useState(false)
  const [recommendations, setRecommendations] = useState([])
  const [error, setError] = useState(null)
  const [hasFetched, setHasFetched] = useState(false)

  const hasData = (tiktokData?.length || 0) + (instaData?.length || 0) > 0

  const handleGenerate = async () => {
    if (!hasData) {
      setError('No social data yet — run the Apify scrape first.')
      return
    }
    setLoading(true)
    setError(null)
    const { recommendations: recs, error: err } = await getCreatorAIInsights({
      creatorName,
      tiktokData: tiktokData || [],
      instaData: instaData || [],
    })
    setLoading(false)
    setHasFetched(true)
    if (err) setError(err)
    else setRecommendations(recs)
  }

  const priorityStyles = {
    high:   { bg: 'rgba(249,115,22,0.08)',  border: 'rgba(249,115,22,0.3)',  text: '#f97316' },
    medium: { bg: 'rgba(59,130,246,0.08)',  border: 'rgba(59,130,246,0.3)',  text: '#3b82f6' },
    low:    { bg: 'rgba(139,92,246,0.08)',  border: 'rgba(139,92,246,0.3)',  text: '#8b5cf6' },
  }

  const categoryEmoji = {
    Growth: '📈',
    Content: '🎬',
    Audience: '👥',
    Brand: '🤝',
    Monetization: '💰',
  }

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18 }}>
            AI Strategic Recommendations
          </div>
          <p style={{ color: 'var(--text2)', fontSize: 12, margin: '2px 0 0' }}>
            Claude-generated, specific to this creator
          </p>
        </div>
        <button
          onClick={handleGenerate}
          disabled={loading || !hasData}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: 8,
            background: loading ? 'var(--bg3)' : 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: loading || !hasData ? 'not-allowed' : 'pointer',
            opacity: !hasData ? 0.5 : 1,
            fontFamily: 'var(--font-body)',
            transition: 'transform 0.1s',
          }}
          onMouseDown={e => { if (!loading && hasData) e.currentTarget.style.transform = 'scale(0.97)' }}
          onMouseUp={e => e.currentTarget.style.transform = ''}
        >
          {loading ? 'Generating...' : hasFetched ? 'Regenerate' : '✨ Generate'}
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px 14px',
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 10,
          color: '#ef4444',
          fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{
          padding: '24px 16px',
          textAlign: 'center',
          color: 'var(--text3)',
          fontSize: 13,
          background: 'var(--bg2)',
          borderRadius: 10,
        }}>
          Analyzing posts and generating recommendations...
        </div>
      )}

      {!loading && recommendations.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 10 }}>
          {recommendations.map((rec, i) => {
            const colors = priorityStyles[rec.priority] || priorityStyles.medium
            return (
              <div key={i} style={{
                background: colors.bg,
                border: `1px solid ${colors.border}`,
                borderRadius: 'var(--radius-lg, 12px)',
                padding: '14px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 18 }}>{categoryEmoji[rec.category] || '💡'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 10, color: 'var(--text3)', fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '0.4px'
                    }}>
                      {rec.category || 'Strategy'}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: colors.text, marginTop: 2 }}>
                      {rec.title}
                    </div>
                  </div>
                </div>
                {rec.insight && (
                  <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, marginBottom: 6, paddingLeft: 26 }}>
                    {rec.insight}
                  </div>
                )}
                {rec.action && (
                  <div style={{
                    fontSize: 12, color: 'var(--text)', lineHeight: 1.5, paddingLeft: 26,
                    borderLeft: `2px solid ${colors.border}`,
                    marginLeft: 13, paddingTop: 2, paddingBottom: 2,
                  }}>
                    <span style={{ fontWeight: 600, color: colors.text }}>Action: </span>{rec.action}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {!loading && hasFetched && recommendations.length === 0 && !error && (
        <div style={{
          padding: '16px',
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          color: 'var(--text3)',
          fontSize: 13,
        }}>
          No recommendations generated. Try again in a moment.
        </div>
      )}
    </div>
  )
}
